import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, isPlatformSuperAdmin, session, SuperAdminConsoleRefusal, type CurrentUser, type LoginPayload } from '@/core';
import * as authApi from '@/api/auth';

/** 423 应答若带解锁时刻则以其为准，缺失时按 §6.0-④ 冻结的 15min 名义窗口兜底 */
function serverUnlockAt(err: ApiError): string {
  const payload = err.payload;
  if (payload && typeof payload === 'object') {
    const raw = (payload as Record<string, unknown>).unlockAt ?? (payload as Record<string, unknown>).lockedUntil;
    if (typeof raw === 'string' && raw) return raw;
    if (typeof raw === 'number' && raw > 0) return new Date(raw).toISOString();
  }
  return new Date(Date.now() + session.ACCOUNT_LOCK_MINUTES * 60 * 1000).toISOString();
}

/** auth store — 登录态 / 令牌 / 会话 30min 超时 / 登录失败锁定倒计时 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(session.getSessionUser());
  const expiresAt = ref<number>(session.sessionExpiresAt());
  const loading = ref(false);
  const lockedUntil = ref<number>(session.lockedUntil());
  const failCount = ref(0);

  const isAuthenticated = computed(() => !!user.value && !!session.getAccessToken());
  const permissions = computed(() => user.value?.permissions ?? []);

  const lockRemainingSeconds = computed(() => {
    const left = lockedUntil.value - Date.now();
    return left > 0 ? Math.ceil(left / 1000) : 0;
  });

  async function login(payload: LoginPayload): Promise<CurrentUser> {
    loading.value = true;
    try {
      const result = await authApi.login(payload);
      // 第8条（用户 m00163）：平台超管登录后看到的是平台后台那一套，本端不提供该界面 —— 从登录这一步就明确拒绝。
      // 判据在落令牌之前：⛔ 不留令牌、⛔ 不留快照，也就没有「能进但看不到东西」的中间态。
      if (isPlatformSuperAdmin(result.user)) throw new SuperAdminConsoleRefusal();
      session.saveSession(result);
      session.resetLoginFailure();
      user.value = result.user;
      expiresAt.value = session.sessionExpiresAt();
      failCount.value = 0;
      lockedUntil.value = 0;
      return result.user;
    } catch (err) {
      // 第8条：超管拒绝不是登录失败 —— 不计入本地失败次数与锁定窗口，原样上抛给登录页
      if (err instanceof SuperAdminConsoleRefusal) throw err;
      // §6.0-④：423 由服务端权威判定，本地必须同步锁定窗口，否则刷新即解锁、继续重试只会延长
      const locked = err instanceof ApiError && err.isAccountLocked;
      const unlockAt = locked ? serverUnlockAt(err) : undefined;
      const info = session.recordLoginFailure(unlockAt);
      failCount.value = info.count;
      lockedUntil.value = info.lockedUntil;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout(callApi = true): Promise<void> {
    if (callApi) {
      try {
        await authApi.logout();
      } catch {
        /* 登出失败不阻塞本地清理 */
      }
    }
    session.clearSession();
    user.value = null;
    expiresAt.value = 0;
    snapshotState.value = 'pending';
  }

  function forceLogout(): void {
    session.clearSession();
    user.value = null;
    expiresAt.value = 0;
    snapshotState.value = 'pending';
    const redirect = encodeURIComponent(window.location.hash.replace(/^#/, '') || '/couriers');
    window.location.hash = `#/login?redirect=${redirect}&reason=timeout`;
  }

  function restore(): CurrentUser | null {
    const stored = session.getSessionUser();
    user.value = stored;
    expiresAt.value = session.sessionExpiresAt();
    return stored;
  }

  /** §6.2-7 快照同步状态：'pending' 未发起 / 'ok' 已按 profile 重建 / 'stale' 端点不可用（沿用 interim 快照） */
  const snapshotState = ref<'pending' | 'ok' | 'stale'>('pending');
  const snapshotError = ref('');

  /**
   * §3.5-② / §6.2-7：刷新页面后**必须**用 `GET /auth/profile` 重建权限态，
   * 不得沿用 localStorage 旧快照（`permVersion` 可能已变）。
   * 端点不可用时不静默假装已刷新：保留登录时服务端签发的 interim 快照并记原因，供界面提示「权限待确认」。
   */
  async function syncProfile(): Promise<void> {
    if (snapshotState.value === 'ok' || !session.getAccessToken()) return;
    try {
      const fresh = await authApi.fetchProfile();
      // t122：profile 面**不下发**首登位（后端只把它放在登录响应里）⇒ 重建权限态时必须**保留**登录时带下来的
      // forcePwdChange，否则刷新页面会把它抹成 undefined，一次 reload 就绕过强制改密拦截。
      const merged = {
        ...fresh,
        forcePwdChange: user.value?.forcePwdChange === true,
        // 跨端登录隔离：profile 可能来自旧后端（无 console 键），保留登录时从令牌带下来的归属。
        console: fresh.console ?? user.value?.console ?? null,
      };
      user.value = merged;
      session.saveSessionUser(merged);
      expiresAt.value = session.sessionExpiresAt();
      snapshotState.value = 'ok';
      snapshotError.value = '';
      // permVersion 变化时无需手动失效缓存：permission store 直接读 user.permissions，已是新值
    } catch (err) {
      snapshotState.value = 'stale';
      snapshotError.value = err instanceof Error ? err.message : String(err);
    }
  }

  /** 供界面/排查判断当前权限态是否已由 §6.2-7 重建 */
  const snapshotFresh = computed(() => snapshotState.value === 'ok');

  /** t122：改密成功后本地清位（服务端已清成员表首登位；快照同步置 false ⇒ 守卫不再拦）。 */
  function clearForcePwdChange(): void {
    if (!user.value) return;
    user.value = { ...user.value, forcePwdChange: false };
    session.saveSessionUser(user.value);
  }

  return {
    user,
    expiresAt,
    loading,
    failCount,
    lockedUntil,
    lockRemainingSeconds,
    isAuthenticated,
    permissions,
    login,
    logout,
    forceLogout,
    restore,
    syncProfile,
    clearForcePwdChange,
    snapshotState,
    snapshotError,
    snapshotFresh,
  };
});
